// src/new/components/monitor/TriageChat/vedaBackend.ts

import { TriageContext } from './triageContext';
import { TriageBackend, HistoryMessage } from './triageBackend';
import { createVedaSession, getVedaSessionMessages } from './vedaSession';

/**
 * Build the veda-ai `TriageBackend` implementation — a thin wrapper around
 * the existing `vedaSession.ts` functions, moving the current veda-ai path
 * behind the strategy interface without changing its behavior.
 * `baseUrl`/`token` are captured as plain params, same as
 * `createExpressBackend` (see expressBackend.ts), and closed over by the
 * returned methods.
 */
export function createVedaBackend(baseUrl: string, token: string): TriageBackend {
  return {
    kind: 'veda',

    // Unchanged from the current (pre-strategy) prefix — existing cached
    // veda-ai session ids in users' browsers must keep resolving under the
    // same key. express-code uses a distinct 'triage.express.session' prefix
    // (see expressBackend.ts) precisely so the two paths never collide.
    storageKeyPrefix: 'triage.session',

    async createSession(_ctx: TriageContext): Promise<{ sessionId: string }> {
      const sessionId = await createVedaSession(baseUrl, token);
      return { sessionId };
    },

    async getHistory(sessionId: string): Promise<HistoryMessage[]> {
      const messages = await getVedaSessionMessages(baseUrl, token, sessionId);
      // vedaSession.ts's own HistoryMessage has `role: 'user' | 'assistant' |
      // 'tool'` and no `subagents` field (veda-ai has no subagent concept).
      // Task D1's HistoryMessage now permits the same `role` union, so
      // tool-call entries pass through unchanged instead of being dropped.
      // `subagents` isn't set here — veda-ai has no subagent concept — so it
      // stays `undefined`.
      return messages.map((m) => ({ role: m.role, text: m.text }));
    },

    // veda-ai's actual stop-generation mechanism is `useVedaChatSocket`'s
    // `stopGeneration()` — a message sent over the already-open WebSocket,
    // which is implicitly scoped to one session, not an HTTP call keyed by
    // session id. vedaSession.ts exports exactly `createVedaSession`,
    // `getVedaSessionMessages`, and `vedaWebSocketUrl` — no abort/stop
    // primitive exists for this backend-strategy layer to delegate to, since
    // it has no access to the live socket. This is intentionally a silent
    // no-op rather than a throw: the caller (Task E1, not yet started) must
    // keep calling `socket.stopGeneration()` directly for the veda-ai path
    // rather than routing through `backend.abortSession()`.
    async abortSession(_sessionId: string): Promise<void> {},
  };
}
